import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/lib/api';

const SITE_URL = 'https://crossfit-saintchamond.fr';
const DEFAULT_OG_IMAGE = 'https://customer-assets.emergentagent.com/job_063f6b75-858e-4561-abbf-3c8b3baa3d11/artifacts/hmmuue2f_WhatsApp%20Image%202026-04-02%20at%2014.38.19.jpeg';
const LOGO = 'https://customer-assets.emergentagent.com/job_crossfit-showcase/artifacts/f7rp3nhg_image.png';

export function PageMeta({ title, description, path, image, noindex }) {
  const siteName = 'CrossFit Saint-Chamond';
  const fullTitle = title ? `${title} | ${siteName} — HYROX Training Club` : `${siteName} — HYROX Training Club | Salle de CrossFit 42400`;
  const desc = description || 'CrossFit Saint-Chamond & HYROX Training Club. Salle de CrossFit à Saint-Chamond (42400, Loire). Coaching sportif, WOD, HYROX, Team WOD. 7 Rue Jean-Jacques Rousseau. À partir de 59€/mois.';
  const url = path ? `${SITE_URL}${path}` : SITE_URL;
  const ogImage = image || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <html lang="fr" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="CrossFit Saint-Chamond" />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content="CrossFit Saint-Chamond — HYROX Training Club" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <>
          <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
          <meta name="googlebot" content="index, follow" />
        </>
      )}

      {/* Geo */}
      <meta name="geo.placename" content="Saint-Chamond, Loire, France" />
      <meta name="geo.region" content="FR-42" />
      <meta name="geo.position" content="45.4783;4.5170" />
      <meta name="ICBM" content="45.4783, 4.5170" />

      <meta name="author" content="CrossFit Saint-Chamond" />
      <meta
        name="keywords"
        content="crossfit saint-chamond, hyrox saint-chamond, salle de crossfit 42400, crossfit loire, crossfit vallée du gier, coaching sportif saint-chamond, wod, hyrox training club, salle de sport saint-chamond, crossfit près de saint-étienne, préparation physique saint-chamond, cours essai gratuit crossfit, crossfit femme saint-chamond, crossfit 42, box crossfit loire"
      />
    </Helmet>
  );
}

/**
 * Injected on every public page: provides Organization, LocalBusiness (ExerciseGym),
 * WebSite, and aggregate review signals to Google.
 */
export function LocalBusinessJsonLd({ telephone, googleBusinessUrl }) {
  const gymSchema = {
    "@context": "https://schema.org",
    "@type": ["ExerciseGym", "SportsActivityLocation", "LocalBusiness"],
    "@id": `${SITE_URL}/#gym`,
    "name": "CrossFit Saint-Chamond",
    "alternateName": ["HYROX Training Club Saint-Chamond", "CrossFit SC", "CFSC", "Box CrossFit Saint-Chamond"],
    "description": "Salle de CrossFit et HYROX Training Club à Saint-Chamond (42400, Loire). Coaching sportif professionnel, entraînements fonctionnels, accessible à tous. WOD, HYROX, Team WOD, Free Access. À 12 km de Saint-Étienne.",
    "url": SITE_URL,
    "logo": LOGO,
    "image": [DEFAULT_OG_IMAGE, LOGO],
    "slogan": "Le bien-être par le sport",
    ...(telephone ? { "telephone": telephone } : {}),
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "7 Rue Jean-Jacques Rousseau",
      "addressLocality": "Saint-Chamond",
      "postalCode": "42400",
      "addressRegion": "Loire",
      "addressCountry": "FR"
    },
    "geo": { "@type": "GeoCoordinates", "latitude": 45.4783, "longitude": 4.5170 },
    "email": "crossfit.saintchamond@gmail.com",
    "sameAs": [
      "https://www.instagram.com/crossfit_saintchamond",
      "https://www.facebook.com/CrossFitSaintChamond",
      ...(googleBusinessUrl ? [googleBusinessUrl] : []),
    ],
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Thursday","Friday"], "opens": "09:00", "closes": "20:30" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": "Wednesday", "opens": "09:00", "closes": "20:30" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "09:30", "closes": "11:30" }
    ],
    "priceRange": "59€ - 99€/mois",
    "currenciesAccepted": "EUR",
    "paymentAccepted": "Espèces, Carte bancaire, Virement",
    "areaServed": [
      { "@type": "City", "name": "Saint-Chamond", "postalCode": "42400" },
      { "@type": "City", "name": "Saint-Étienne", "postalCode": "42000" },
      { "@type": "City", "name": "L'Horme", "postalCode": "42152" },
      { "@type": "City", "name": "Rive-de-Gier", "postalCode": "42800" },
      { "@type": "City", "name": "La Grand-Croix", "postalCode": "42320" },
      { "@type": "City", "name": "Lorette", "postalCode": "42420" },
      { "@type": "City", "name": "La Talaudière", "postalCode": "42350" },
      { "@type": "City", "name": "Sorbiers", "postalCode": "42290" },
      { "@type": "City", "name": "Saint-Jean-Bonnefonds", "postalCode": "42650" },
      { "@type": "City", "name": "Firminy", "postalCode": "42700" },
      { "@type": "City", "name": "Saint-Paul-en-Jarez", "postalCode": "42740" },
      { "@type": "City", "name": "Cellieu", "postalCode": "42320" },
      { "@type": "AdministrativeArea", "name": "Vallée du Gier" },
      { "@type": "AdministrativeArea", "name": "Loire (42)" }
    ],
    "knowsAbout": [
      "CrossFit", "HYROX", "Functional Training", "WOD", "Team WOD",
      "Coaching sportif", "Préparation physique", "Entraînement fonctionnel",
      "Haltérophilie", "Musculation fonctionnelle", "Gymnastique", "Cardio"
    ],
    "founder": { "@type": "Person", "name": "Nicolas Perroy", "jobTitle": "Fondateur & Coach CrossFit certifié" },
    "employee": { "@type": "Person", "name": "Nicolas Perroy", "jobTitle": "Coach principal" },
    "legalName": "NP Activités Physiques Sports Santé",
    "vatID": "FR88101447928",
    "taxID": "10144792800017",
    "iso6523Code": "0002:101447928",
    "identifier": [
      { "@type": "PropertyValue", "propertyID": "SIREN", "value": "101447928" },
      { "@type": "PropertyValue", "propertyID": "SIRET", "value": "10144792800017" },
      { "@type": "PropertyValue", "propertyID": "RCS", "value": "Saint-Étienne 101 447 928" },
      { "@type": "PropertyValue", "propertyID": "NAF", "value": "85.51Z" }
    ],
    "potentialAction": [
      {
        "@type": "ReserveAction",
        "name": "Réserver un cours",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${SITE_URL}/reservation`,
          "actionPlatform": [
            "https://schema.org/DesktopWebPlatform",
            "https://schema.org/MobileWebPlatform"
          ]
        },
        "result": { "@type": "Reservation", "name": "Réservation cours CrossFit / HYROX" }
      },
      {
        "@type": "BuyAction",
        "name": "Acheter un abonnement en ligne",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${SITE_URL}/abonnements-en-ligne`,
          "actionPlatform": [
            "https://schema.org/DesktopWebPlatform",
            "https://schema.org/MobileWebPlatform"
          ]
        }
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Abonnements CrossFit Saint-Chamond",
      "itemListElement": [
        { "@type": "Offer", "name": "Abonnement 1x/Semaine", "price": "59", "priceCurrency": "EUR", "description": "1 cours par semaine, accès WOD et HYROX" },
        { "@type": "Offer", "name": "Abonnement 2x/Semaine", "price": "69", "priceCurrency": "EUR", "description": "2 cours par semaine, accès WOD et HYROX" },
        { "@type": "Offer", "name": "Abonnement 3x/Semaine", "price": "89", "priceCurrency": "EUR", "description": "3 cours par semaine, Free Access inclus" },
        { "@type": "Offer", "name": "Abonnement Illimité", "price": "99", "priceCurrency": "EUR", "description": "Cours illimités, Free Access, Team WOD" }
      ]
    },
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "bestRating": "5", "worstRating": "1", "reviewCount": "23", "ratingCount": "23" }
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    "name": "CrossFit Saint-Chamond",
    "url": SITE_URL,
    "logo": LOGO,
    "sameAs": gymSchema.sameAs,
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    "url": SITE_URL,
    "name": "CrossFit Saint-Chamond",
    "publisher": { "@id": `${SITE_URL}/#organization` },
    "inLanguage": "fr-FR",
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(gymSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
    </Helmet>
  );
}

/**
 * SiteWideSchema — drop-in <SiteWideSchema /> on every public page.
 * Fetches contact-info (for telephone/GBP URL) and injects LocalBusiness schema.
 */
export function SiteWideSchema() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    axios.get(`${API}/content/contact-info`).then((r) => setInfo(r.data)).catch(() => setInfo({}));
  }, []);
  if (!info) return null;
  return <LocalBusinessJsonLd telephone={info.phone || null} googleBusinessUrl={info.google_business_url || null} />;
}

export function FAQJsonLd({ faqs }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.q || faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.a || faq.answer },
    })),
    // Speakable: signals to voice assistants (Siri, Alexa, Google Assistant)
    // which parts of the page are best suited for read-aloud answers.
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["h1", "h2", ".faq-question", ".faq-answer"],
      "xpath": ["/html/body//h1", "/html/body//h2"],
    },
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function BreadcrumbJsonLd({ items }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "name": item.name,
      ...(item.url ? { "item": `${SITE_URL}${item.url}` } : {}),
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

/**
 * ReviewsJsonLd — fetches testimonials from CMS and exposes them as Review schema.
 * Boosts eligibility for star-rating rich snippets in Google results.
 */
export function ReviewsJsonLd() {
  const [reviews, setReviews] = useState(null);
  useEffect(() => {
    axios.get(`${API}/content/testimonials`).then((r) => setReviews(r.data || [])).catch(() => setReviews([]));
  }, []);
  if (!reviews || reviews.length === 0) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "ExerciseGym",
    "@id": `${SITE_URL}/#gym`,
    "review": reviews.slice(0, 10).map((t) => ({
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": String(t.rating || 5),
        "bestRating": "5",
        "worstRating": "1",
      },
      "author": { "@type": "Person", "name": t.name },
      "reviewBody": t.text,
      ...(t.role ? { "publisher": { "@type": "Organization", "name": t.role } } : {}),
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
